import { useBrowserAgentSearchModeEnabled } from '#pplx/frontend/apps/ask/hooks/comet-hooks/useBrowserAgentSearchModeEnabled.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useGateSearchModelSelection } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useGateSearchModelSelection.js';
import { useSharedSelectSearchModel } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useSharedSelectSearchModel.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { usePreferredSearchModels } from '#pplx/frontend/apps/ask/settings/hooks/usePreferredSearchModels.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchMode, SearchModel, searchModeSupportsModelSelection, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useCallback, useMemo } from 'react';
export const useAskInputSearchModel = ({ searchMode, searchModel: controlledSearchModel, onSearchModelSelect: controlledOnSearchModelSelect, onSearchModelDeselect: controlledOnSearchModelDeselect, }) => {
    const { configuredModel, compareModels, setCompareModels, setConfiguredModel, initialConfiguredModelIsLoading, } = useThreadConfig();
    const { clearPreferredSearchModel } = usePreferredSearchModels();
    const { getDefaultModelForMode } = useSearchModels({ reason: 'ask-input-search-model' });
    const openPaywallUpsell = usePaywallUpsell();
    const setSelectedSearchModel = useSharedSelectSearchModel({
        openPaywallUpsell,
    });
    const { gateSearchModelSelection } = useGateSearchModelSelection({ openPaywallUpsell });
    const { shouldShowModelSelector } = useBrowserAgentSearchModeEnabled();
    const enabled = searchMode === SearchMode.BROWSER_AGENT
        ? shouldShowModelSelector
        : searchModeSupportsModelSelection(searchMode);
    const handleModelDeselect = useCallback(() => {
        if (controlledOnSearchModelDeselect) {
            controlledOnSearchModelDeselect();
            return;
        }
        const defaultAsiModel = getDefaultModelForMode(SearchMode.ASI);
        clearPreferredSearchModel(SearchMode.ASI);
        if (controlledOnSearchModelSelect) {
            controlledOnSearchModelSelect(defaultAsiModel);
        }
        else {
            setConfiguredModel(defaultAsiModel);
        }
    }, [
        getDefaultModelForMode,
        clearPreferredSearchModel,
        controlledOnSearchModelDeselect,
        controlledOnSearchModelSelect,
        setConfiguredModel,
    ]);
    return useMemo(() => {
        if (searchMode === SearchMode.AGENTIC_RESEARCH) {
            const props = {
                enabled,
                multiple: true,
                models: compareModels,
                onModelsChange: setCompareModels,
                maxModels: 3,
                minModels: 2,
            };
            return props;
        }
        const model = controlledSearchModel ?? configuredModel;
        const onModelChange = (model) => {
            const isGated = gateSearchModelSelection(model);
            if (isGated) {
                return false;
            }
            if (controlledOnSearchModelSelect) {
                controlledOnSearchModelSelect(model);
                return true;
            }
            return setSelectedSearchModel(
            // this `as SearchModel` is part of the dynamic model config migration
            // the new `SearchModelMenu` component uses the dynamic config and uses type `string` for models
            model);
        };
        const props = {
            enabled,
            multiple: false,
            model,
            onModelChange,
            onModelDeselect: searchMode === SearchMode.ASI ? handleModelDeselect : undefined,
            deferInitialModelValidation: controlledSearchModel === undefined &&
                controlledOnSearchModelSelect === undefined &&
                initialConfiguredModelIsLoading,
        };
        return props;
    }, [
        searchMode,
        enabled,
        controlledSearchModel,
        controlledOnSearchModelSelect,
        configuredModel,
        initialConfiguredModelIsLoading,
        handleModelDeselect,
        setSelectedSearchModel,
        gateSearchModelSelection,
        compareModels,
        setCompareModels,
    ]);
};
//# sourceMappingURL=useAskInputSearchModel.js.map